/**
 * Admin bracket tree (INT-0037) — renders playoff slots in the SAME
 * two-sided bracket shape as the public PlayoffBracket (consolation side
 * flowing left, winners flowing right to a Champion endpoint, CSS elbow
 * connectors), so admins see the shape they're publishing while they drag
 * games between slots.
 *
 * Geometry comes from the shared `buildBracketLayout`
 * (packages/shared/utils/bracketLayout.ts — the canonical copy of the
 * public app's layout math), so the two surfaces cannot drift in shape.
 * The connector/column DOM mirrors apps/app PlayoffBracket.tsx's
 * Connector/MatchColumn, restyled with admin tokens (ADR 0057) instead of
 * the public gray palette.
 *
 * This component owns ONLY the shape: what goes inside each slot is the
 * caller's `renderSlot` (DivisionPage injects its draggable/editable
 * GameSlot cards, keeping the `slot-playoff-<key>` drag contract and
 * ADM-05-10 semantics untouched).
 */
import * as React from 'react';
import { buildBracketLayout, type BracketSlotLike } from '@staty/shared/utils/bracketLayout';

export interface BracketTreeSlot extends BracketSlotLike {
  group: string;
}

function Connector({ count, side }: { count: number; side: 'left' | 'right' }) {
  const open = side === 'left' ? 'rounded-l-md border-y border-l' : 'rounded-r-md border-y border-r';
  const stub = side === 'left' ? '-left-3 right-auto' : '-right-3';
  return (
    <div aria-hidden="true" className="flex w-6 shrink-0 flex-col self-stretch">
      <div className="mb-1 h-6" />
      <div className="flex flex-1 flex-col justify-around">
        {Array.from({ length: Math.max(1, count) }).map((_, i) => (
          <div key={i} className="flex flex-1 items-center">
            <div className={`relative h-[55%] w-3 border-border ${open}`}>
              <div className={`absolute top-1/2 w-3 border-t border-border ${stub}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BracketTree({
  slots,
  renderSlot,
}: {
  slots: BracketTreeSlot[];
  renderSlot: (slot: BracketTreeSlot) => React.ReactNode;
}) {
  const layout = buildBracketLayout(slots);

  const matchBox = (slot: BracketSlotLike) => (
    <div key={slot.key} className="flex flex-1 flex-col justify-center py-3">
      {renderSlot(slot as BracketTreeSlot)}
    </div>
  );

  const col = (
    column: { title: string; slots: BracketSlotLike[]; corner?: BracketSlotLike | null },
    key: string,
  ) => (
    <div key={key} className="flex w-56 shrink-0 flex-col">
      <div
        data-testid="bracket-col-title"
        className="mb-1 flex h-6 items-end justify-center text-[11px] font-bold uppercase tracking-wide text-muted-foreground"
      >
        {column.title}
      </div>
      <div className="flex flex-1 flex-col justify-around">{column.slots.map(matchBox)}</div>
      {column.corner ? <div className="mt-4 border-t border-border/60 pt-2">{matchBox(column.corner)}</div> : null}
    </div>
  );

  return (
    <div className="overflow-x-auto" data-testid="bracket-tree">
      <div className="flex min-w-max items-stretch px-2 pb-3 pt-2">
        {layout.leftColumns.map((column) => (
          <React.Fragment key={`L-${column.title}`}>
            {col(column, `Lc-${column.title}`)}
            <Connector side="left" count={column.slots.length} />
          </React.Fragment>
        ))}
        {col({ title: layout.centerTitle, slots: layout.center, corner: layout.centerCorner }, 'center')}
        {layout.rightColumns.map((column) => (
          <React.Fragment key={`R-${column.title}`}>
            <Connector side="right" count={column.slots.length} />
            {col(column, `Rc-${column.title}`)}
          </React.Fragment>
        ))}
        {layout.championLabel ? (
          <>
            <Connector side="right" count={1} />
            <div className="flex shrink-0 flex-col justify-center pl-1">
              <div className="text-center text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {layout.championLabel}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
